import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  MapPin, 
  User, 
  Search, 
  Navigation, 
  CheckCircle,
  ArrowRight,
  Loader,
  X,
  Heart
} from 'lucide-react'
import toast from 'react-hot-toast'

interface BirthData {
  name: string;
  day: number;
  month: number;
  year: number;
  hour: number;
  min: number;
  lat: number;
  lon: number;
  tzone: number;
  place_of_birth: string;
}

interface InteractiveMatchmakingFormProps {
  onComplete?: (maleData: BirthData, femaleData: BirthData) => void
  onCancel?: () => void
}

const InteractiveMatchmakingForm: React.FC<InteractiveMatchmakingFormProps> = ({ 
  onComplete, 
  onCancel 
}) => {
  const [step, setStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [currentPartner, setCurrentPartner] = useState<'male' | 'female'>('male')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [selectedLocation, setSelectedLocation] = useState<any>(null)
  
  const [maleData, setMaleData] = useState<Partial<BirthData>>({
    name: '',
    place_of_birth: '',
    lat: 0,
    lon: 0,
    day: 1,
    month: 1,
    year: 1990,
    hour: 12,
    min: 0,
    tzone: 5.5
  })

  const [femaleData, setFemaleData] = useState<Partial<BirthData>>({
    name: '',
    place_of_birth: '',
    lat: 0,
    lon: 0,
    day: 1,
    month: 1,
    year: 1990,
    hour: 12,
    min: 0,
    tzone: 5.5
  })

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Step titles
  const steps = [
    { id: 1, title: 'Male Partner', icon: User },
    { id: 2, title: 'Female Partner', icon: User },
    { id: 3, title: 'Analysis', icon: Heart }
  ]

  // Get current partner data
  const getCurrentData = () => currentPartner === 'male' ? maleData : femaleData
  const setCurrentData = (data: Partial<BirthData>) => {
    if (currentPartner === 'male') {
      setMaleData(data)
    } else {
      setFemaleData(data)
    }
  }

  // Location search with debouncing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (searchQuery.length > 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchLocations(searchQuery)
      }, 500)
    } else {
      setSearchResults([])
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery])

  const searchLocations = async (query: string) => {
    try {
      setIsLoading(true)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`
      )
      const data = await response.json()
      setSearchResults(data)
    } catch (error) {
      console.error('Error searching locations:', error)
      toast.error('Failed to search locations')
    } finally {
      setIsLoading(false)
    }
  }

  const selectLocation = (location: any) => {
    setSelectedLocation(location)
    setCurrentData({
      ...getCurrentData(),
      place_of_birth: location.display_name,
      lat: parseFloat(location.lat),
      lon: parseFloat(location.lon)
    })
    setSearchQuery(location.display_name)
    setSearchResults([])
  }

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsLoading(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          )
            .then(response => response.json())
            .then(data => {
              selectLocation({
                display_name: data.display_name,
                lat: latitude.toString(),
                lon: longitude.toString()
              })
              toast.success('Location detected!')
            })
            .catch(() => {
              toast.error('Could not get location name')
            })
            .finally(() => setIsLoading(false))
        },
        () => {
          toast.error('Could not get your location')
          setIsLoading(false)
        }
      )
    } else {
      toast.error('Geolocation not supported')
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setCurrentData({ ...getCurrentData(), [field]: value })
  }

  const nextStep = () => {
    if (step < 3) {
      setStep(step + 1)
      if (step === 1) {
        setCurrentPartner('female')
        setSelectedLocation(null)
        setSearchQuery('')
      }
    }
  }

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1)
      if (step === 2) {
        setCurrentPartner('male')
        setSelectedLocation(null)
        setSearchQuery('')
      }
    }
  }

  const handleSubmit = async () => {
    console.log('handleSubmit called with data:', { maleData, femaleData })
    
    if (!maleData.name || !femaleData.name || !maleData.place_of_birth || !femaleData.place_of_birth) {
      console.error('Validation failed:', { 
        maleName: maleData.name, 
        femaleName: femaleData.name, 
        malePlace: maleData.place_of_birth, 
        femalePlace: femaleData.place_of_birth 
      })
      toast.error('Please fill in all required fields')
      return
    }

    try {
      setIsLoading(true)
      const completeMaleData: BirthData = {
        name: maleData.name!,
        place_of_birth: maleData.place_of_birth!,
        lat: maleData.lat || 0,
        lon: maleData.lon || 0,
        day: maleData.day!,
        month: maleData.month!,
        year: maleData.year!,
        hour: maleData.hour!,
        min: maleData.min!,
        tzone: maleData.tzone!
      }

      const completeFemaleData: BirthData = {
        name: femaleData.name!,
        place_of_birth: femaleData.place_of_birth!,
        lat: femaleData.lat || 0,
        lon: femaleData.lon || 0,
        day: femaleData.day!,
        month: femaleData.month!,
        year: femaleData.year!,
        hour: femaleData.hour!,
        min: femaleData.min!,
        tzone: femaleData.tzone!
      }

      console.log('Calling onComplete with:', { completeMaleData, completeFemaleData })
      // Keep loader active until the parent finishes the async analysis
      await Promise.resolve(onComplete?.(completeMaleData, completeFemaleData))
    } catch (error) {
      console.error('Error preparing matchmaking data:', error)
      toast.error('Failed to prepare matchmaking data')
    } finally {
      setIsLoading(false)
    }
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="w-full max-w-2xl bg-gradient-to-br from-slate-900 via-purple-900/20 to-pink-900/20 rounded-3xl border border-white/10 backdrop-blur-xl p-6 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Matchmaking Analysis</h2>
          <button
            onClick={onCancel}
            className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((stepItem) => {
            const Icon = stepItem.icon
            const isActive = step === stepItem.id
            const isCompleted = step > stepItem.id
            
            return (
              <div key={stepItem.id} className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isCompleted 
                    ? 'bg-green-500' 
                    : isActive 
                      ? 'bg-purple-500' 
                      : 'bg-white/20'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="w-5 h-5 text-white" />
                  ) : (
                    <Icon className="w-5 h-5 text-white" />
                  )}
                </div>
                <span className={`text-xs mt-2 ${
                  isActive ? 'text-purple-400' : 'text-gray-400'
                }`}>
                  {stepItem.title}
                </span>
              </div>
            )
          })}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Male Partner */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">Male Partner Details</h3>
                <p className="text-gray-300">Enter the birth details for the male partner</p>
              </div>

              {/* Name */}
              <div>
                <label className="block text-white font-medium mb-2">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={maleData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter male partner's full name"
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-white font-medium mb-2">Birth Location</label>
                
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for male partner's birth place..."
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                  {isLoading && (
                    <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-400 animate-spin" />
                  )}
                </div>

                <button
                  onClick={getCurrentLocation}
                  disabled={isLoading}
                  className="w-full mb-4 mobile-button-secondary flex items-center justify-center space-x-2"
                >
                  <Navigation className="w-4 h-4" />
                  <span>Use Current Location</span>
                </button>

                {/* Search Results */}
                <AnimatePresence>
                  {searchResults.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="max-h-48 overflow-y-auto space-y-2"
                    >
                      {searchResults.map((result, index) => (
                        <button
                          key={index}
                          onClick={() => selectLocation(result)}
                          className="w-full p-3 text-left bg-white/5 hover:bg-white/10 rounded-xl transition-colors border border-white/10"
                        >
                          <div className="flex items-start space-x-3">
                            <MapPin className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="text-white font-medium text-sm">
                                {result.display_name.split(',')[0]}
                              </div>
                              <div className="text-gray-400 text-xs">
                                {result.display_name}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Selected Location */}
                {selectedLocation && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-4 bg-green-500/20 border border-green-500/30 rounded-xl"
                  >
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <div>
                        <div className="text-white font-medium">Location Selected</div>
                        <div className="text-gray-300 text-sm">{selectedLocation.display_name}</div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Birth Date</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={maleData.day}
                      onChange={(e) => handleInputChange('day', parseInt(e.target.value))}
                      placeholder="Day"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                    <select
                      value={maleData.month}
                      onChange={(e) => handleInputChange('month', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    >
                      {months.map((month, index) => (
                        <option key={index + 1} value={index + 1} className="bg-slate-800">
                          {month}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="1900"
                      max={new Date().getFullYear()}
                      value={maleData.year}
                      onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
                      placeholder="Year"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Birth Time (24h)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={maleData.hour}
                      onChange={(e) => handleInputChange('hour', parseInt(e.target.value))}
                      placeholder="Hour"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={maleData.min}
                      onChange={(e) => handleInputChange('min', parseInt(e.target.value))}
                      placeholder="Min"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={nextStep}
                disabled={!maleData.name?.trim() || !selectedLocation}
                className="w-full mobile-button-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Continue to Female Partner</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}

          {/* Step 2: Female Partner */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">Female Partner Details</h3>
                <p className="text-gray-300">Enter the birth details for the female partner</p>
              </div>

              {/* Name */}
              <div>
                <label className="block text-white font-medium mb-2">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={femaleData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter female partner's full name"
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-white font-medium mb-2">Birth Location</label>
                
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for female partner's birth place..."
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                  {isLoading && (
                    <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-400 animate-spin" />
                  )}
                </div>

                <button
                  onClick={getCurrentLocation}
                  disabled={isLoading}
                  className="w-full mb-4 mobile-button-secondary flex items-center justify-center space-x-2"
                >
                  <Navigation className="w-4 h-4" />
                  <span>Use Current Location</span>
                </button>

                {/* Search Results */}
                <AnimatePresence>
                  {searchResults.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="max-h-48 overflow-y-auto space-y-2"
                    >
                      {searchResults.map((result, index) => (
                        <button
                          key={index}
                          onClick={() => selectLocation(result)}
                          className="w-full p-3 text-left bg-white/5 hover:bg-white/10 rounded-xl transition-colors border border-white/10"
                        >
                          <div className="flex items-start space-x-3">
                            <MapPin className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="text-white font-medium text-sm">
                                {result.display_name.split(',')[0]}
                              </div>
                              <div className="text-gray-400 text-xs">
                                {result.display_name}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Selected Location */}
                {selectedLocation && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-4 bg-green-500/20 border border-green-500/30 rounded-xl"
                  >
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <div>
                        <div className="text-white font-medium">Location Selected</div>
                        <div className="text-gray-300 text-sm">{selectedLocation.display_name}</div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Birth Date</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={femaleData.day}
                      onChange={(e) => handleInputChange('day', parseInt(e.target.value))}
                      placeholder="Day"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                    <select
                      value={femaleData.month}
                      onChange={(e) => handleInputChange('month', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    >
                      {months.map((month, index) => (
                        <option key={index + 1} value={index + 1} className="bg-slate-800">
                          {month}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="1900"
                      max={new Date().getFullYear()}
                      value={femaleData.year}
                      onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
                      placeholder="Year"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Birth Time (24h)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={femaleData.hour}
                      onChange={(e) => handleInputChange('hour', parseInt(e.target.value))}
                      placeholder="Hour"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={femaleData.min}
                      onChange={(e) => handleInputChange('min', parseInt(e.target.value))}
                      placeholder="Min"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={prevStep}
                  className="flex-1 mobile-button-secondary"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!femaleData.name?.trim() || !selectedLocation}
                  className="flex-1 mobile-button-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Review & Analyze</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Review & Submit */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">Review Details</h3>
                <p className="text-gray-300">Confirm the details before analysis</p>
              </div>

              {/* Male Partner Summary */}
              <div className="bg-white/5 p-4 rounded-xl">
                <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Male Partner
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Name:</span>
                    <span className="text-white ml-2">{maleData.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Location:</span>
                    <span className="text-white ml-2">{maleData.place_of_birth}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Birth Date:</span>
                    <span className="text-white ml-2">{maleData.day}/{maleData.month}/{maleData.year}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Birth Time:</span>
                    <span className="text-white ml-2">{maleData.hour}:{maleData.min}</span>
                  </div>
                </div>
              </div>

              {/* Female Partner Summary */}
              <div className="bg-white/5 p-4 rounded-xl">
                <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Female Partner
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Name:</span>
                    <span className="text-white ml-2">{femaleData.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Location:</span>
                    <span className="text-white ml-2">{femaleData.place_of_birth}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Birth Date:</span>
                    <span className="text-white ml-2">{femaleData.day}/{femaleData.month}/{femaleData.year}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Birth Time:</span>
                    <span className="text-white ml-2">{femaleData.hour}:{femaleData.min}</span>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={prevStep}
                  className="flex-1 mobile-button-secondary"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 mobile-button-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <span>Analyze Compatibility</span>
                      <Heart className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}

export default InteractiveMatchmakingForm 